/**
 * Settings configuration types for the v3 dashboard.
 * Used by both the leads worker (backend) and dashboard (frontend).
 */

import type { Plan } from './plans'

// ============================================================
// Setting Keys
// ============================================================

export const SETTING_KEYS = {
  offer: 'offer_config',
  messages: 'message_config',
  cta: 'cta_config',
  content: 'content_config',
  senderVoice: 'sender_voice_config',
  workflowSafety: 'workflow_safety_config',
  faq: 'faq_entries',
  objections: 'objection_entries',
} as const

/** Minimum plan required to access each setting group */
export const SETTINGS_PLAN_REQUIRED: Record<string, Plan> = {
  [SETTING_KEYS.offer]: 'starter',
  [SETTING_KEYS.messages]: 'starter',
  [SETTING_KEYS.cta]: 'starter',
  // 2026-07-27 owner spec: content posting opens to every plan. Starter gets
  // 5 posts/week with 1 automated; Pro gets 10/week with 1 automated per day.
  [SETTING_KEYS.content]: 'starter',
  [SETTING_KEYS.senderVoice]: 'pro',
  [SETTING_KEYS.workflowSafety]: 'starter',
  [SETTING_KEYS.faq]: 'starter',
  [SETTING_KEYS.objections]: 'starter',
}

// ============================================================
// FAQ Config — questions a lead might ask + how the AI should answer
// ============================================================

/**
 * One FAQ entry. The AI consults these on inbound replies (task='reply')
 * and uses a matching answer as the source of truth when the lead asks
 * something similar.
 */
export type FaqEntry = {
  readonly id: string
  readonly question: string
  readonly answer: string
}

export type FaqConfig = {
  readonly entries: readonly FaqEntry[]
}

export const DEFAULT_FAQ: FaqConfig = {
  entries: [],
}

/**
 * Runtime type guard — used by PUT /settings/faq to validate body shape.
 * Trims strings and enforces non-empty question + answer for each entry,
 * caps total entries at 100 (sanity bound on prompt-injected context),
 * and per-field length limits matched to what realistically fits in the
 * AI worker's system prompt without blowing the context budget.
 */
export function isFaqConfig(value: unknown): value is FaqConfig {
  if (typeof value !== 'object' || value === null) return false
  const v = value as { entries?: unknown }
  if (!Array.isArray(v.entries)) return false
  if (v.entries.length > 100) return false
  for (const entry of v.entries) {
    if (typeof entry !== 'object' || entry === null) return false
    const e = entry as { id?: unknown; question?: unknown; answer?: unknown }
    if (typeof e.id !== 'string' || e.id.length === 0 || e.id.length > 64) return false
    if (typeof e.question !== 'string' || e.question.trim().length === 0 || e.question.length > 500) return false
    if (typeof e.answer !== 'string' || e.answer.trim().length === 0 || e.answer.length > 4000) return false
  }
  return true
}

// ============================================================
// Objection Config — objections the lead might raise + how the AI counters
// ============================================================

/**
 * One Objection entry — a likely-to-be-raised concern with the response
 * the AI should mirror when it comes up. Distinct from FAQ in two ways:
 *   1. FAQ is for direct questions ("How much does it cost?"). Objections
 *      are for stated concerns / pushback ("This feels like an AI message",
 *      "We've tried this and it didn't work").
 *   2. Objections are matched at REPLY time even when the lead's message
 *      doesn't look like a question, because objections are often
 *      statements, not questions.
 */
export type ObjectionEntry = {
  readonly id: string
  readonly objection: string
  readonly response: string
}

export type ObjectionConfig = {
  readonly entries: readonly ObjectionEntry[]
}

/**
 * Default seed. We always ship the "this feels like AI" objection because
 * it's the single most common pushback we see across F&F. Users can edit
 * or remove it; the response below is the wording Eli + TJ approved
 * 2026-06-03 — direct, confident, doesn't apologize for being automated.
 */
export const DEFAULT_OBJECTIONS: ObjectionConfig = {
  entries: [
    {
      id: 'obj_default_ai_detection',
      objection: 'This feels like an AI automated message to me.',
      response:
        'I promise you the amount of sales closed and time saved far outweigh the amount of people who can detect this is AI.',
    },
  ],
}

export function isObjectionConfig(value: unknown): value is ObjectionConfig {
  if (typeof value !== 'object' || value === null) return false
  const v = value as { entries?: unknown }
  if (!Array.isArray(v.entries)) return false
  if (v.entries.length > 100) return false
  for (const entry of v.entries) {
    if (typeof entry !== 'object' || entry === null) return false
    const e = entry as { id?: unknown; objection?: unknown; response?: unknown }
    if (typeof e.id !== 'string' || e.id.length === 0 || e.id.length > 64) return false
    if (typeof e.objection !== 'string' || e.objection.trim().length === 0 || e.objection.length > 500) return false
    if (typeof e.response !== 'string' || e.response.trim().length === 0 || e.response.length > 4000) return false
  }
  return true
}

// ============================================================
// Offer & Pitch Config
// ============================================================

export type OfferConfig = {
  readonly offerDescription: string
  readonly primaryPainPoint: string
  readonly painPoints: readonly string[]
  readonly socialProof: string
  readonly differentiator: string
  readonly coreOfferOneLiner: string
  readonly hasCaseStudy: boolean
  readonly caseStudyOneLiner: string
  readonly hasShareableResource: boolean
  readonly resourceDescription: string
  readonly resourceLink: string
  readonly authorityProof: string
  readonly targetAudience?: string
}

export const DEFAULT_OFFER: OfferConfig = {
  offerDescription: '',
  primaryPainPoint: '',
  painPoints: [],
  socialProof: '',
  differentiator: '',
  coreOfferOneLiner: '',
  hasCaseStudy: false,
  caseStudyOneLiner: '',
  hasShareableResource: false,
  resourceDescription: '',
  resourceLink: '',
  authorityProof: '',
  targetAudience: '',
}

// ============================================================
// Message Settings
// ============================================================

export type MessageTone = 'professional_casual' | 'casual' | 'professional' | 'formal'

export type MessageConfig = {
  readonly messageTone: MessageTone
  readonly exampleMessage: string
  readonly customInstructions: string
  readonly messagesPerHour: number
  readonly maxDailyMessages: number
  readonly followUp1Days: number
  readonly followUp2Days: number
  readonly followUp3Days: number
}

export const DEFAULT_MESSAGES: MessageConfig = {
  messageTone: 'professional_casual',
  exampleMessage: '',
  customInstructions: '',
  messagesPerHour: 5,
  maxDailyMessages: 30,
  followUp1Days: 3,
  followUp2Days: 7,
  followUp3Days: 14,
}

// ============================================================
// CTA Config
// ============================================================

export type CtaType =
  | 'book_demo'
  | 'book_discovery_call'
  | 'book_strategy_call'
  | 'free_audit'
  | 'send_to_page'
  | 'send_to_trial'
  | 'send_to_event'
  | 'direct_conversation'
  | 'calendar_link'

export const CTA_TYPES: readonly CtaType[] = [
  'book_demo',
  'book_discovery_call',
  'book_strategy_call',
  'free_audit',
  'send_to_page',
  'send_to_trial',
  'send_to_event',
  'direct_conversation',
  'calendar_link',
]

/**
 * Canonical CTA label per type. This is what the model is told to ask the
 * lead for (the `{cta}` prompt variable in outreach + the "Campaign call to
 * action" line in the reply prompt). Owner directive 2026-08-30: the CTA
 * and its link are campaign-level (campaigns.settings.cta_type +
 * offer_link), never an account-wide Offer setting.
 */
export const CTA_TYPE_LABELS: Record<CtaType, string> = {
  book_demo: 'book a quick demo',
  book_discovery_call: 'book a discovery call',
  book_strategy_call: 'book a strategy call',
  free_audit: 'claim a free audit',
  send_to_page: 'take a look at the page',
  send_to_trial: 'start a free trial',
  send_to_event: 'register for the event',
  direct_conversation: 'keep the conversation going here',
  calendar_link: 'grab a time on the calendar',
}

/** Friendly option labels for the campaign wizard / Edit Campaign select. */
export const CTA_TYPE_UI_LABELS: Record<CtaType, string> = {
  book_demo: 'Book a Demo',
  book_discovery_call: 'Book a Discovery Call',
  book_strategy_call: 'Book a Strategy Call',
  free_audit: 'Free Audit',
  send_to_page: 'Send to Landing Page',
  send_to_trial: 'Send to Free Trial',
  send_to_event: 'Send to Event',
  direct_conversation: 'Direct Conversation (no link)',
  calendar_link: 'Calendar Link',
}

/**
 * CTA types whose link is a "go look at this" URL rather than a booking
 * page. The reply generator pastes these as soon as the lead says they are
 * interested (positive_interested), not only on an explicit meeting yes.
 */
export const CTA_LINK_ON_INTEREST_TYPES: readonly CtaType[] = [
  'send_to_page',
  'send_to_trial',
  'send_to_event',
  'free_audit',
]

export function isCtaType(v: unknown): v is CtaType {
  return typeof v === 'string' && (CTA_TYPES as readonly string[]).includes(v)
}

/** Per-campaign CTA, stored in campaigns.settings JSON. */
export type CampaignCta = {
  readonly cta_type: CtaType
  /** Empty string only when cta_type === 'direct_conversation'. */
  readonly offer_link: string
}

// ============================================================
// Campaign resources — additional links/attachments with usage notes
// ============================================================

/**
 * One campaign resource (owner request 2026-09-01): an ADDITIONAL link the
 * AI may send mid-conversation, with a plain-text description of WHEN to
 * use it (e.g. a signup page "send when the lead is interested" plus a
 * meeting link "send in the final farewell DM"). Stored as
 * `campaigns.settings.resources` — an array of up to
 * MAX_CAMPAIGN_RESOURCES entries. The PRIMARY CTA link stays
 * settings.cta_type + settings.offer_link; resources never replace it.
 */
export type CampaignResource = {
  readonly url: string
  readonly label: string
  readonly description: string
}

export const MAX_CAMPAIGN_RESOURCES = 5
export const CAMPAIGN_RESOURCE_LABEL_MAX = 60
export const CAMPAIGN_RESOURCE_DESCRIPTION_MAX = 300

/**
 * Strict validator for the campaigns POST/PUT routes. Returns either the
 * trimmed, normalized array or a human-readable error the route can 400
 * with. The url must be http(s) because the AI pastes it verbatim into
 * LinkedIn messages; label/description are free text bounded so the prompt
 * block they render into stays small.
 */
export function validateCampaignResources(value: unknown):
  | { readonly ok: true; readonly resources: readonly CampaignResource[] }
  | { readonly ok: false; readonly error: string } {
  if (!Array.isArray(value)) {
    return { ok: false, error: 'settings.resources must be an array' }
  }
  if (value.length > MAX_CAMPAIGN_RESOURCES) {
    return { ok: false, error: `settings.resources supports at most ${MAX_CAMPAIGN_RESOURCES} entries` }
  }
  const out: CampaignResource[] = []
  for (let i = 0; i < value.length; i++) {
    const entry = value[i] as { url?: unknown; label?: unknown; description?: unknown } | null
    if (typeof entry !== 'object' || entry === null) {
      return { ok: false, error: `settings.resources[${i}] must be an object with url, label, description` }
    }
    const url = typeof entry.url === 'string' ? entry.url.trim() : ''
    if (!/^https?:\/\/\S+$/.test(url)) {
      return { ok: false, error: `settings.resources[${i}].url must be an http(s) URL` }
    }
    const label = typeof entry.label === 'string' ? entry.label.trim() : null
    if (label === null || label.length > CAMPAIGN_RESOURCE_LABEL_MAX) {
      return { ok: false, error: `settings.resources[${i}].label must be a string of at most ${CAMPAIGN_RESOURCE_LABEL_MAX} characters` }
    }
    const description = typeof entry.description === 'string' ? entry.description.trim() : null
    if (description === null || description.length > CAMPAIGN_RESOURCE_DESCRIPTION_MAX) {
      return { ok: false, error: `settings.resources[${i}].description must be a string of at most ${CAMPAIGN_RESOURCE_DESCRIPTION_MAX} characters` }
    }
    out.push({ url, label, description })
  }
  return { ok: true, resources: out }
}

/**
 * Lenient runtime reader for stored settings blobs. Legacy or hand-edited
 * rows may hold junk — runtime consumers (orchestrator pipeline, reply
 * generator, preview, normalizeCampaign) drop invalid entries instead of
 * erroring, because a malformed resource must never stall a send.
 */
export function parseCampaignResources(value: unknown): readonly CampaignResource[] {
  if (!Array.isArray(value)) return []
  const out: CampaignResource[] = []
  for (const entry of value) {
    if (out.length >= MAX_CAMPAIGN_RESOURCES) break
    const e = entry as { url?: unknown; label?: unknown; description?: unknown } | null
    if (typeof e !== 'object' || e === null) continue
    const url = typeof e.url === 'string' ? e.url.trim() : ''
    if (!/^https?:\/\/\S+$/.test(url)) continue
    out.push({
      url,
      label: typeof e.label === 'string' ? e.label.trim().slice(0, CAMPAIGN_RESOURCE_LABEL_MAX) : '',
      description: typeof e.description === 'string' ? e.description.trim().slice(0, CAMPAIGN_RESOURCE_DESCRIPTION_MAX) : '',
    })
  }
  return out
}

export type CtaConfig = {
  readonly ctaType: CtaType
  readonly ctaLink: string
  readonly desiredAction: string
  readonly ctaPrimaryLabel: string
  readonly ctaPrimaryUrl: string
  readonly ctaSecondaryLabel: string
  readonly ctaSecondaryUrl: string
}

export const DEFAULT_CTA: CtaConfig = {
  ctaType: 'book_demo',
  ctaLink: '',
  desiredAction: '',
  ctaPrimaryLabel: '',
  ctaPrimaryUrl: '',
  ctaSecondaryLabel: '',
  ctaSecondaryUrl: '',
}

// ============================================================
// Content Config (all plans since 2026-07-27; auto-poster cadence is
// clamped per plan by autoPostsPerWeek)
// ============================================================

export type ContentConfig = {
  readonly contentTopics: readonly string[]
  readonly contentTone: string
  readonly postingFrequency: string
  readonly contentPillars: readonly string[]
  readonly contentCtaMode: string
  readonly posterEnabled: boolean
  readonly posterFrequency: string
  readonly posterAutoApprove: boolean
  /** Standing style guidance for generated posts, saved from the auto-poster
   *  preview/feedback flow. Applied to every generation for this user. */
  readonly posterStyleNotes?: string
}

export const DEFAULT_CONTENT: ContentConfig = {
  contentTopics: [],
  contentTone: 'professional',
  postingFrequency: 'daily',
  contentPillars: [],
  contentCtaMode: 'subtle',
  posterEnabled: false,
  posterFrequency: 'daily',
  posterAutoApprove: false,
  posterStyleNotes: '',
}

// ============================================================
// Sender Voice Config (Pro+)
// ============================================================

export type SenderVoiceConfig = {
  readonly profileSummary: string
  readonly writingSample1: string
  readonly writingSample2: string
  readonly povAreas: readonly string[]
  readonly credibilityBullets: readonly string[]
  readonly doNotSay: readonly string[]
}

export const DEFAULT_SENDER_VOICE: SenderVoiceConfig = {
  profileSummary: '',
  writingSample1: '',
  writingSample2: '',
  povAreas: [],
  credibilityBullets: [],
  doNotSay: [],
}

// ============================================================
// Workflow & Safety Config
// ============================================================

/**
 * Manual message review mode (owner request 2026-07-22):
 *   'all'     — every AI-written message (outreach, follow-ups, replies)
 *               waits in the inbox review queue until approved.
 *   'flagged' — only outreach drafts the quality gate scores in the
 *               send_with_review band wait for approval; high-scoring
 *               drafts and AI replies send automatically.
 *   'none'    — fully automatic (default, matches historical behavior).
 * Connection invites carry no text (deliberate) and are never held.
 */
export type ReviewMode = 'all' | 'flagged' | 'none'

export const REVIEW_MODES: readonly ReviewMode[] = ['all', 'flagged', 'none']

export type WorkflowSafetyConfig = {
  readonly warmupEnabled: boolean
  readonly aiReplyEnabled: boolean
  readonly posterEnabled: boolean
  readonly commentDmEnabled: boolean
  readonly maxDailyConnections: number
  readonly maxDailyProfileViews: number
  readonly weekendEnabled: boolean
  readonly rampupEnabled: boolean
  readonly rampupDays: number
  readonly offHoursStart: number
  readonly offHoursEnd: number
  readonly reviewMode: ReviewMode
}

export const DEFAULT_WORKFLOW_SAFETY: WorkflowSafetyConfig = {
  warmupEnabled: true,
  aiReplyEnabled: false,
  posterEnabled: false,
  commentDmEnabled: false,
  maxDailyConnections: 20,
  maxDailyProfileViews: 50,
  weekendEnabled: false,
  rampupEnabled: true,
  rampupDays: 14,
  offHoursStart: 22,
  offHoursEnd: 7,
  reviewMode: 'none',
}

// ============================================================
// Validators
// ============================================================

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((item) => typeof item === 'string')
}

export function isOfferConfig(v: unknown): v is OfferConfig {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return (
    typeof o.offerDescription === 'string' &&
    typeof o.primaryPainPoint === 'string' &&
    isStringArray(o.painPoints) &&
    typeof o.socialProof === 'string' &&
    typeof o.differentiator === 'string' &&
    typeof o.coreOfferOneLiner === 'string' &&
    typeof o.hasCaseStudy === 'boolean' &&
    typeof o.hasShareableResource === 'boolean'
  )
}

export function isMessageConfig(v: unknown): v is MessageConfig {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return (
    typeof o.messageTone === 'string' &&
    typeof o.messagesPerHour === 'number' &&
    typeof o.maxDailyMessages === 'number' &&
    typeof o.followUp1Days === 'number' &&
    typeof o.followUp2Days === 'number' &&
    typeof o.followUp3Days === 'number'
  )
}

export function isCtaConfig(v: unknown): v is CtaConfig {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return typeof o.ctaType === 'string' && typeof o.ctaLink === 'string'
}

export function isContentConfig(v: unknown): v is ContentConfig {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return (
    isStringArray(o.contentTopics) &&
    typeof o.contentTone === 'string' &&
    typeof o.posterEnabled === 'boolean' &&
    (o.posterStyleNotes === undefined || typeof o.posterStyleNotes === 'string')
  )
}

export function isSenderVoiceConfig(v: unknown): v is SenderVoiceConfig {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return (
    typeof o.profileSummary === 'string' &&
    isStringArray(o.povAreas) &&
    isStringArray(o.credibilityBullets) &&
    isStringArray(o.doNotSay)
  )
}

export function isWorkflowSafetyConfig(v: unknown): v is WorkflowSafetyConfig {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return (
    typeof o.warmupEnabled === 'boolean' &&
    typeof o.aiReplyEnabled === 'boolean' &&
    typeof o.maxDailyConnections === 'number' &&
    typeof o.maxDailyProfileViews === 'number' &&
    // Optional for backward compatibility: blobs saved before 0031 (and
    // stale dashboard tabs) omit it; the PUT route backfills from the
    // stored blob so an old client can't silently reset the mode.
    (o.reviewMode === undefined || REVIEW_MODES.includes(o.reviewMode as ReviewMode))
  )
}
